import {
  Box,
  ButtonBase,
  Drawer,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { CurrencyIcon, Skeleton } from "../../shared";
import { fetchExchangeRate } from "../../../services/exchange-rate";
import {
  getCryptoOverview,
  type CryptoOverviewItem,
} from "../../../services/crypto";

/**
 * Список крипто-активов пользователя (USDT, BTC …).
 *
 * По умолчанию бэк присылает USDT+BTC (см. seed). Админ может добавлять
 * любые новые монеты в админке — они появятся здесь автоматически.
 *
 * Каждая карточка кликабельная — открывает выбор сети для депозита
 * (WalletActionButtons управляет модалкой, здесь просто навигируемся
 * на страницу депозита конкретной валюты).
 */

/**
 * Дефолтные логотипы «зашитой» крипты — используются как fallback ровно
 * до тех пор, пока админ не задал `Currency.icon` в /crypto. После этого
 * значение из БД имеет приоритет.
 */
const DEFAULT_ICONS: Record<string, string> = {
  USDT: "https://static.coincap.io/assets/icons/usdt@2x.png",
  BTC:  "https://static.coincap.io/assets/icons/btc@2x.png",
  ETH:  "https://static.coincap.io/assets/icons/eth@2x.png",
  SOL:  "https://static.coincap.io/assets/icons/sol@2x.png",
  TRX:  "https://static.coincap.io/assets/icons/trx@2x.png",
  BNB:  "https://static.coincap.io/assets/icons/bnb@2x.png",
};

function AssetIcon({ code, icon }: { code: string; icon: string | null }) {
  // Приоритет: значение из админки (iconify:/URL/base64) → default → буква.
  const value = icon ?? DEFAULT_ICONS[code] ?? null;
  return (
    <CurrencyIcon
      value={value}
      size={40}
      fallbackLetter={code}
      alt={code}
      sx={(theme) => ({
        bgcolor:
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.05)"
            : "rgba(0, 0, 0, 0.05)",
      })}
    />
  );
}

function formatAmount(v: number, decimals: number, locale = "ru-RU"): string {
  if (!Number.isFinite(v) || v === 0) {
    return "0";
  }
  return v.toLocaleString(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: Math.min(decimals, 6),
  });
}

interface AssetRowProps {
  item: CryptoOverviewItem;
  rateToRub: Record<string, number>;
  onNavigated?: () => void;
  /**
   * Кастомный обработчик клика по строке актива.
   * Если задан — вызывается вместо навигации на `/asset/:code`.
   * Используется в deposit-flow (WalletActionButtons), где выбор актива
   * ведёт сразу на депозит (или к выбору сети, если сетей несколько).
   */
  onSelect?: (item: CryptoOverviewItem) => void;
}

function AssetRow({ item, rateToRub, onNavigated, onSelect }: AssetRowProps) {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const locale = i18n.language === "en" ? "en-US" : "ru-RU";
  const amount = Number(item.balance) || 0;

  // Сколько это в рублях: если у нас есть курс — считаем, иначе «—».
  const rate = rateToRub[item.code] ?? 0;
  const rubValue = amount * rate;

  // Строка кликабельна всегда (даже если сетей 0) — на странице актива
  // юзер увидит корректное пустое состояние. Раньше здесь блокировался
  // клик по `!canDeposit`, но теперь конечная точка — не депозит, а
  // страница актива, где полно информации без обязательного адреса.
  return (
    <ButtonBase
      onClick={() => {
        if (onSelect) {
          // Родитель сам управляет переходом (например, deposit-flow —
          // выбор валюты → сеть). Клик просто пробрасывается наверх.
          onSelect(item);
          return;
        }
        // По умолчанию клик по активу открывает его отдельную
        // страницу — как это принято у крупных кошельков (Bybit,
        // OKX, Trust). Там уже есть баланс, действия, кошельки по
        // сетям и история.
        navigate(`/asset/${encodeURIComponent(item.code)}`);
        onNavigated?.();
      }}
      sx={(theme) => ({
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: "14px",
        padding: "12px 14px",
        borderRadius: "18px",
        bgcolor: "secondary.main",
        border:
          theme.palette.mode === "light"
            ? "1px solid rgba(0, 0, 0, 0.05)"
            : "none",
        transition: "background 0.15s",
        "&:active": {
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.06)"
              : "rgba(0, 0, 0, 0.03)",
        },
      })}
    >
      <AssetIcon code={item.code} icon={item.icon} />

      <Box sx={{ flex: 1, textAlign: "left", minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "15px",
            fontWeight: 600,
            color: "text.primary",
            lineHeight: 1.2,
          }}
        >
          {item.name}
        </Typography>
        <Typography
          sx={{
            fontSize: "12px",
            color: "text.secondary",
            marginTop: "2px",
          }}
        >
          {item.code}
        </Typography>
      </Box>

      <Box sx={{ textAlign: "right" }}>
        <Typography
          sx={{
            fontSize: "15px",
            fontWeight: 700,
            color: "text.primary",
            lineHeight: 1.2,
          }}
        >
          {formatAmount(amount, item.decimals, locale)}{" "}
          <Box component="span" sx={{ opacity: 0.6, fontWeight: 500 }}>
            {item.code}
          </Box>
        </Typography>
        <Typography
          sx={{
            fontSize: "12px",
            color: "text.secondary",
            marginTop: "2px",
          }}
        >
          {rubValue > 0 ? `≈ ${formatAmount(rubValue, 0, locale)} ₽` : "—"}
        </Typography>
      </Box>
    </ButtonBase>
  );
}

// ---------------------------------------------------------------
// Полноэкранный drawer со списком активов и поиском сверху.
// ---------------------------------------------------------------

export interface AssetsSearchDrawerProps {
  open: boolean;
  onClose: () => void;
  items: CryptoOverviewItem[];
  rateToRub: Record<string, number>;
  /**
   * Если задан — при клике по активу вызывается этот колбэк вместо
   * навигации на /deposit. Drawer при этом закрывается.
   */
  onSelectAsset?: (item: CryptoOverviewItem) => void;
  /** Заголовок можно переопределить (напр. «Выберите валюту»). */
  title?: string;
  /** Подсказка над списком (напр. «Пополнение»). */
  subtitle?: string;
}

export function AssetsSearchDrawer({
  open,
  onClose,
  items,
  rateToRub,
  onSelectAsset,
  title,
  subtitle,
}: AssetsSearchDrawerProps) {
  const [query, setQuery] = useState("");
  const { t } = useTranslation("home");
  const { t: tc } = useTranslation("common");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (i) =>
        i.code.toLowerCase().includes(q) ||
        i.name.toLowerCase().includes(q),
    );
  }, [items, query]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: 100015 }}
      slotProps={{
        backdrop: { sx: { backgroundColor: "rgba(0, 0, 0, 0.55)" } },
      }}
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: "100%",
          bgcolor: "background.default",
          backgroundImage: "none",
          display: "flex",
          flexDirection: "column",
          // Учитываем системный header Telegram (× Закрыть, статус-бар и
          // safe-area). Без этого заголовок «Мои активы» уезжал под
          // системную панель и сливался с крестиком закрытия.
          padding:
            "calc(env(safe-area-inset-top, 0px) + 16px) 16px calc(env(safe-area-inset-bottom, 0px) + 20px)",
          boxSizing: "border-box",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "14px",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{ color: "text.primary" }}
          aria-label={tc("aria.close")}
        >
          <Icon icon="solar:alt-arrow-left-linear" width={22} height={22} />
        </IconButton>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 600,
              color: "text.primary",
              lineHeight: 1.2,
            }}
          >
            {title ?? t("sections.my_assets")}
          </Typography>
          {subtitle ? (
            <Typography
              sx={{
                fontSize: "12px",
                color: "text.secondary",
                marginTop: "2px",
              }}
            >
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      </Box>

      <TextField
        autoFocus
        fullWidth
        placeholder={t("sections.search_placeholder")}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Icon
                icon="solar:magnifer-linear"
                width={20}
                height={20}
                color="currentColor"
              />
            </InputAdornment>
          ),
          endAdornment: query ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() => setQuery("")}
                sx={{ color: "text.secondary" }}
                aria-label={tc("aria.clear")}
              >
                <Icon icon="solar:close-circle-bold" width={18} height={18} />
              </IconButton>
            </InputAdornment>
          ) : null,
          sx: {
            borderRadius: "14px",
            bgcolor: "secondary.main",
            "& fieldset": { border: "none" },
            fontSize: "15px",
            paddingRight: query ? "6px" : undefined,
          },
        }}
        sx={{ marginBottom: "14px" }}
      />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          overflowY: "auto",
          flex: 1,
          paddingBottom: "8px",
          // Каждая строка должна иметь высоту строго по контенту,
          // без растяжения на всю оставшуюся высоту контейнера.
          "& > *": { flexShrink: 0 },
          // Скрываем скроллбар как в остальном приложении.
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
        }}
      >
        {filtered.length === 0 ? (
          <Box
            sx={{
              padding: "24px 16px",
              borderRadius: "16px",
              bgcolor: "secondary.main",
              textAlign: "center",
              color: "text.secondary",
              fontSize: "14px",
            }}
          >
            {t("sections.nothing_found")}
          </Box>
        ) : (
          filtered.map((item) => (
            <AssetRow
              key={item.code}
              item={item}
              rateToRub={rateToRub}
              onNavigated={onClose}
              onSelect={onSelectAsset}
            />
          ))
        )}
      </Box>
    </Drawer>
  );
}

interface WalletAssetsListProps {
  isLoading?: boolean;
}

export function WalletAssetsList({ isLoading: parentLoading }: WalletAssetsListProps) {
  const navigate = useNavigate();
  const { t } = useTranslation("home");
  const [searchOpen, setSearchOpen] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryFn: getCryptoOverview,
    queryKey: ["crypto-overview"],
    staleTime: 30_000,
    retry: 1,
  });

  // Один общий обработчик — и для карточек списка, и для поиска.
  // Ведёт на детальную страницу актива `/asset/:code`, где юзер
  // получает баланс, действия и кошельки по всем сетям.
  const handleAssetSelect = (item: CryptoOverviewItem) => {
    setSearchOpen(false);
    navigate(`/asset/${encodeURIComponent(item.code)}`);
  };

  // Курс USDT → RUB — используем для расчёта эквивалента.
  const { data: usdtRate } = useQuery({
    queryFn: () => fetchExchangeRate({ fromCurrency: "USDT", toCurrency: "RUB", sellAmount: 1 }),
    queryKey: ["rate", "USDT", "RUB"],
    staleTime: 60_000,
  });

  const usdtRub = Number(usdtRate?.show_rate ?? usdtRate?.rate ?? 0) || 0;
  // BTC → RUB через жёсткий множитель: BTC*100k*USDT_RUB (upper-bound estimate).
  // TODO: как только DV.net expose rates — читать из /store/currency-rate.
  const rateToRub: Record<string, number> = {
    USDT: usdtRub,
    BTC: usdtRub * 100_000,
    ETH: usdtRub * 3_500,
    SOL: usdtRub * 200,
  };

  const loading = parentLoading || isLoading;

  /**
   * Порядок карточек в списке:
   *   1. Активы с положительным рублёвым эквивалентом — по убыванию ₽;
   *   2. Активы с положительным балансом, но без курса (rubValue = 0) —
   *      по убыванию монет, чтобы не терялись мелкие альткоины с балансом;
   *   3. Активы с нулевым балансом — по алфавиту (стабильный порядок).
   *
   * Именно этот порядок используется и в главном списке, и в drawer'е
   * поиска — чтобы «нашлось то же и в той же последовательности».
   */
  const items = useMemo(() => {
    const raw = data ?? [];
    const scored = raw.map((item) => {
      const amount = Number(item.balance);
      const safeAmount = Number.isFinite(amount) ? amount : 0;
      const rate = rateToRub[item.code] ?? 0;
      const rubValue = safeAmount * rate;
      return { item, amount: safeAmount, rubValue };
    });
    scored.sort((a, b) => {
      // 1. По рублёвому эквиваленту (обе > 0).
      if (a.rubValue !== b.rubValue) return b.rubValue - a.rubValue;
      // 2. По балансу без курса.
      if (a.amount !== b.amount) return b.amount - a.amount;
      // 3. Пустые — по алфавиту, USDT/BTC — вперёд как «якорные».
      const priority = (code: string) =>
        code === "USDT" ? 0 : code === "BTC" ? 1 : 2;
      const pa = priority(a.item.code);
      const pb = priority(b.item.code);
      if (pa !== pb) return pa - pb;
      return a.item.code.localeCompare(b.item.code);
    });
    return scored.map((s) => s.item);
    // rateToRub — новый объект на каждый рендер, но зависит только от
    // usdtRub; включаем его в deps через usdtRub, а не сам объект.
  }, [data, usdtRub]);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "12px",
          paddingLeft: "4px",
        }}
      >
        <Typography
          sx={{
            fontSize: "20px",
            fontWeight: 600,
          }}
        >
          {t("sections.my_assets")}
        </Typography>

        <IconButton
          onClick={() => setSearchOpen(true)}
          disabled={loading || items.length === 0}
          sx={{
            width: 36,
            height: 36,
            color: "text.primary",
            bgcolor: "transparent",
            "&:hover": { bgcolor: "transparent" },
            "&.Mui-disabled": { opacity: 0.4, color: "text.primary" },
          }}
          aria-label={t("sections.search_assets_aria")}
        >
          <Icon icon="solar:magnifer-linear" width={22} height={22} />
        </IconButton>
      </Box>

      <Box sx={{ display: "grid", gap: "8px" }}>
        {loading ? (
          <>
            <Skeleton variant="rounded" height={64} sx={{ borderRadius: "18px" }} />
            <Skeleton variant="rounded" height={64} sx={{ borderRadius: "18px" }} />
          </>
        ) : isError || items.length === 0 ? (
          <Box
            sx={{
              padding: "20px 16px",
              borderRadius: "18px",
              bgcolor: "secondary.main",
              textAlign: "center",
            }}
          >
            <Typography sx={{ fontSize: "14px", color: "text.secondary" }}>
              {t("sections.assets_not_configured")}
            </Typography>
          </Box>
        ) : (
          items.map((item) => (
            <AssetRow
              key={item.code}
              item={item}
              rateToRub={rateToRub}
              onSelect={handleAssetSelect}
            />
          ))
        )}
      </Box>

      <AssetsSearchDrawer
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        items={items}
        rateToRub={rateToRub}
        onSelectAsset={handleAssetSelect}
      />
    </Box>
  );
}
